import { zodResolver } from '@hookform/resolvers/zod'
import { DialogContent, Stack } from '@mui/material'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import CustomTypography from '@/components/dataDisplay/CustomTypography'
import FormDialogActions from '@/components/feedback/FormDialogActions'
import SelectFieldController from '@/components/inputs/SelectFieldController'
import TextFieldController from '@/components/inputs/TextFieldController'
import { getCreateActivationGroupSchema } from '@/features/activationGroup/schemas'
import type { CreateActivationGroup } from '@/features/activationGroup/types'
import { ActivationProvider, ApiVolumeRangeUnit, ResourceCategory } from '@/features/activationGroup/types'
import { MarketProgram } from '@/features/bidding/constants'
import type { Portfolio } from '@/features/bidding/types/bid'

type CreateActivationGroupDialogFormProps = {
  portfolios: Portfolio[]
  isLoading: boolean
  onCancel: () => void
  onSubmit: (data: CreateActivationGroup) => void
}

/**
 * Contains all the logic related to the form to create a new activation group.
 *
 * It is used within the CreateActivationGroupDialog component.
 */
const CreateActivationGroupDialogForm = ({
  isLoading,
  onCancel,
  onSubmit,
  portfolios,
}: CreateActivationGroupDialogFormProps) => {
  const { t } = useTranslation()
  const form = useForm({
    defaultValues: {
      name: '',
      portfolio: '',
      activationProvider: ActivationProvider.SYMPOWER_CENTRAL,
      resourceCategory: ResourceCategory.MIXED,
      biddableVolumeRange: {
        min: 0,
        max: 0,
        unit: ApiVolumeRangeUnit.WATTS,
      },
      regulationObjectCode: null,
      transmissionArea: null,
      tradingStrategy: null,
      reportingCode: null,
      externalCode: null,
    },
    resolver: zodResolver(getCreateActivationGroupSchema(t, portfolios)),
  })

  const selectedPortfolioCode = form.watch('portfolio')
  const isFinland = portfolios.find((portfolio) => portfolio.code === selectedPortfolioCode)?.countryCode === 'FI'

  function handleSubmit(newActivationGroup: CreateActivationGroup) {
    // Clear any Finland attribute fields of a group if it is non-FI
    onSubmit(isFinland ? newActivationGroup : { ...newActivationGroup, transmissionArea: null, reportingCode: null })
  }

  return (
    <FormProvider {...form}>
      <form noValidate aria-label={t('activation_groups.create_form.title')} onSubmit={form.handleSubmit(handleSubmit)}>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <TextFieldController required id="name" label={t('common.name')} name="name" />
          <SelectFieldController
            required
            id="portfolio"
            label={t('activation_groups.create_form.portfolio_label')}
            name="portfolio"
            options={
              portfolios
                ?.toSorted((a, b) => a.code.localeCompare(b.code))
                .map((portfolio) => ({
                  value: portfolio.code,
                  label: portfolio.code,
                  id: portfolio.code,
                })) ?? []
            }
          />

          <SelectFieldController
            required
            id="market-program"
            label={t('common.market_program.label')}
            name="marketProgram"
            options={Object.values(MarketProgram).map((marketProgram) => ({
              value: marketProgram,
              label: t(`bidding.market_program.${marketProgram}`),
              id: marketProgram,
            }))}
          />

          <SelectFieldController
            required
            id="activation-provider"
            label={t('activation_groups.activation_provider_label')}
            name="activationProvider"
            options={Object.values(ActivationProvider).map((provider) => ({
              value: provider,
              label: t(
                `activation_groups.activation_provider.${provider.toLowerCase() as Lowercase<ActivationProvider>}`,
              ),
              description: t(
                `activation_groups.activation_provider_description.${provider.toLowerCase() as Lowercase<ActivationProvider>}`,
              ),
              id: provider,
            }))}
          />

          <SelectFieldController
            required
            id="resource-category"
            label={t('activation_groups.create_form.resource_category_label')}
            name="resourceCategory"
            options={Object.values(ResourceCategory).map((category) => ({
              value: category,
              label: t(`activation_groups.resource_category.${category.toLowerCase() as Lowercase<ResourceCategory>}`),
              id: category,
            }))}
          />

          <Stack flexDirection="column" gap={3}>
            <CustomTypography variant="h3">{t('activation_groups.create_form.bidding_limits_label')}</CustomTypography>

            <Stack flexDirection="column" gap={3}>
              <TextFieldController
                id="biddable-volume-range-min"
                label={t('activation_groups.create_form.biddable_volume_range_min_label')}
                name="biddableVolumeRange.min"
                type="number"
              />

              <TextFieldController
                id="biddable-volume-range-max"
                label={t('activation_groups.create_form.biddable_volume_range_max_label')}
                name="biddableVolumeRange.max"
                type="number"
              />

              <SelectFieldController
                fullWidth
                id="biddable-volume-range-unit"
                label={t('activation_groups.create_form.biddable_volume_range_unit_label')}
                name="biddableVolumeRange.unit"
                options={Object.values(ApiVolumeRangeUnit).map((unit) => ({
                  value: unit,
                  label: t(
                    `activation_groups.volume_range_units.${unit.toLowerCase() as Lowercase<ApiVolumeRangeUnit>}`,
                  ),
                  id: unit,
                }))}
              />
            </Stack>
          </Stack>

          <Stack flexDirection="column" gap={3}>
            <CustomTypography variant="h3">
              {t('activation_groups.create_form.market_attributes_label')}
            </CustomTypography>

            <TextFieldController
              id="regulation-object-code"
              label={t('activation_groups.create_form.regulation_object_code_label')}
              name="regulationObjectCode"
              type="text"
            />

            <TextFieldController
              id="trading-strategy"
              label={t('activation_groups.create_form.trading_strategy_label')}
              name="tradingStrategy"
              type="text"
            />

            <TextFieldController
              id="external-code"
              label={t('activation_groups.create_form.external_code_label')}
              name="externalCode"
              type="text"
            />
          </Stack>

          {isFinland && (
            <Stack flexDirection="column" gap={3}>
              <CustomTypography variant="h3">
                {t('activation_groups.create_form.finland_attributes_label')}
              </CustomTypography>

              <SelectFieldController
                fullWidth
                id="transmission-area"
                label={t('activation_groups.create_form.transmission_area_label')}
                name="transmissionArea"
                options={['north', 'central', 'south'].map((value) => ({
                  id: value,
                  value,
                  label: t(`activation_groups.create_form.transmission_area.${value as 'north' | 'central' | 'south'}`),
                }))}
              />

              <TextFieldController
                id="reporting-code"
                label={t('activation_groups.create_form.reporting_code_label')}
                name="reportingCode"
                type="text"
              />
            </Stack>
          )}
        </DialogContent>

        <FormDialogActions isLoading={isLoading} onCancel={onCancel} />
      </form>
    </FormProvider>
  )
}

export default CreateActivationGroupDialogForm
